/**
 * Collection redesign behaviours.
 *
 * Two custom elements, no dependencies, no framework. Both are defined defensively so the file can be loaded
 * from more than one section without throwing on re-definition.
 */

if (!customElements.get("read-more")) {
  customElements.define(
    "read-more",
    class ReadMore extends HTMLElement {
      connectedCallback() {
        this.body = this.querySelector("[data-read-more-body]");
        this.toggle = this.querySelector("[data-read-more-toggle]");

        if (!this.body || !this.toggle) return;

        // Only offer the toggle when the copy is actually clamped, otherwise it is noise.
        requestAnimationFrame(() => {
          if (this.body.scrollHeight - this.body.clientHeight > 2) {
            this.toggle.hidden = false;
          }
        });

        this.toggle.addEventListener("click", this.#onToggle);
      }

      disconnectedCallback() {
        this.toggle?.removeEventListener("click", this.#onToggle);
      }

      #onToggle = () => {
        const expanded = this.toggleAttribute("open");

        this.toggle.setAttribute("aria-expanded", String(expanded));
        this.querySelector("[data-read-more-label-more]").hidden = expanded;
        this.querySelector("[data-read-more-label-less]").hidden = !expanded;
      };
    },
  );
}

if (!customElements.get("model-selector")) {
  customElements.define(
    "model-selector",
    class ModelSelector extends HTMLElement {
      connectedCallback() {
        this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
        this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
        this.items = Array.from(this.querySelectorAll("[data-model-item]"));
        this.groups = Array.from(this.querySelectorAll("[data-model-group]"));
        this.empty = this.querySelector("[data-model-empty]");
        this.search = this.querySelector("[data-model-search]");
        this.cta = this.querySelector("[data-model-cta]");
        this.ctaLabel = this.querySelector("[data-model-cta-label]");
        this.ctaTemplate = this.dataset.ctaTemplate || "Browse {model}";

        this.addEventListener("click", this.#onClick);
        this.addEventListener("keydown", this.#onKeyDown);
        this.search?.addEventListener("input", this.#onSearch);

        this.#revealActiveModel();
      }

      disconnectedCallback() {
        this.removeEventListener("click", this.#onClick);
        this.removeEventListener("keydown", this.#onKeyDown);
        this.search?.removeEventListener("input", this.#onSearch);
        clearTimeout(this.searchTimeout);
      }

      /** Open on the brand the shopper is already browsing rather than always on the first tab. */
      #revealActiveModel() {
        const current = this.querySelector("[data-model-item].is-selected");
        if (!current) return;

        const panel = current.closest('[role="tabpanel"]');
        const tab = this.tabs.find(
          (item) => item.getAttribute("aria-controls") === panel?.id,
        );

        if (tab) this.#selectTab(tab, { focus: false });
      }

      #onClick = (event) => {
        const tab = event.target.closest('[role="tab"]');

        if (tab) {
          this.#selectTab(tab, { focus: true });
          return;
        }

        const item = event.target.closest("[data-model-item]");
        if (!item) return;

        // Let the browser handle new tab / new window intents untouched.
        if (
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.altKey ||
          event.button !== 0
        )
          return;

        // Second click on the current selection means "go", so nobody is trapped hunting for the footer.
        if (item.classList.contains("is-selected")) return;

        event.preventDefault();
        this.#selectModel(item);
      };

      #onKeyDown = (event) => {
        if (!event.target.matches('[role="tab"]')) return;

        const offset = {
          ArrowDown: 1,
          ArrowRight: 1,
          ArrowUp: -1,
          ArrowLeft: -1,
          Home: -Infinity,
          End: Infinity,
        }[event.key];
        if (offset === undefined) return;

        event.preventDefault();

        const index = this.tabs.indexOf(event.target);
        const next = Math.min(
          Math.max(index + offset, 0),
          this.tabs.length - 1,
        );

        this.#selectTab(this.tabs[next], { focus: true });
      };

      #selectTab(tab, { focus }) {
        this.tabs.forEach((item) => {
          const selected = item === tab;

          item.setAttribute("aria-selected", String(selected));
          item.tabIndex = selected ? 0 : -1;
        });

        this.panels.forEach((panel) => {
          panel.hidden = panel.id !== tab.getAttribute("aria-controls");
        });

        if (focus) tab.focus();
      }

      #selectModel(item) {
        this.items.forEach((other) => {
          other.classList.toggle("is-selected", other === item);

          if (other === item) {
            other.setAttribute("aria-current", "true");
          } else {
            other.removeAttribute("aria-current");
          }
        });

        if (!this.cta) return;

        this.cta.href = item.href;
        this.ctaLabel.textContent = this.ctaTemplate.replace(
          "all",
          item.dataset.modelTitle || item.textContent.trim(),
        );
        this.cta.removeAttribute("aria-disabled");
      }

      #onSearch = () => {
        clearTimeout(this.searchTimeout);
        this.searchTimeout = setTimeout(
          () => this.#applySearch(this.search.value.trim().toLowerCase()),
          120,
        );
      };

      #applySearch(query) {
        // Searching flattens the brand rail: shoppers type "s25" without caring which brand it sits under.
        if (!query) {
          this.removeAttribute("data-searching");
          this.items.forEach((item) => (item.hidden = false));
          this.groups.forEach((group) => (group.hidden = false));
          this.panels.forEach((panel) => {
            const tab = this.tabs.find(
              (item) => item.getAttribute("aria-controls") === panel.id,
            );
            panel.hidden = tab?.getAttribute("aria-selected") !== "true";
          });
          if (this.empty) this.empty.hidden = true;
          return;
        }

        this.setAttribute("data-searching", "");

        const tokens = query.split(/\s+/);
        let matches = 0;

        this.items.forEach((item) => {
          const haystack =
            item.dataset.searchTerm || item.textContent.toLowerCase();
          const hit = tokens.every((token) => haystack.includes(token));

          item.hidden = !hit;
          if (hit) matches += 1;
        });

        this.panels.forEach((panel) => (panel.hidden = false));
        this.groups.forEach((group) => {
          group.hidden = !group.querySelector(
            "[data-model-item]:not([hidden])",
          );
        });

        if (this.empty) this.empty.hidden = matches > 0;
      }
    },
  );
}

/**
 * <facet-panel> — the inline Filter dropdown on desktop.
 *
 * The important detail: facets-form with update-on-change re-renders the section on every change, which destroys
 * and rebuilds this element. Open state therefore cannot live in a JS variable — it is persisted per collection
 * so the panel survives ticking a filter. Without this, the panel slams shut on every click.
 */
if (!customElements.get("facet-panel")) {
  customElements.define(
    "facet-panel",
    class FacetPanel extends HTMLElement {
      static storageKey = "facet-panel-open";

      connectedCallback() {
        this.toggles = Array.from(
          document.querySelectorAll("[data-facet-panel-toggle]"),
        );

        this.toggles.forEach((toggle) =>
          toggle.addEventListener("click", this.#onToggle),
        );
        document.addEventListener("keydown", this.#onKeyDown);
        document.addEventListener("click", this.#onDocumentClick);

        if (
          sessionStorage.getItem(FacetPanel.storageKey) ===
          window.location.pathname
        ) {
          this.#setOpen(true, { persist: false, focus: false });
        }
      }

      disconnectedCallback() {
        this.toggles.forEach((toggle) =>
          toggle.removeEventListener("click", this.#onToggle),
        );
        document.removeEventListener("keydown", this.#onKeyDown);
        document.removeEventListener("click", this.#onDocumentClick);
      }

      get open() {
        return !this.hidden;
      }

      #onToggle = () =>
        this.#setOpen(!this.open, { persist: true, focus: true });

      #onKeyDown = (event) => {
        if (event.key !== "Escape" || !this.open) return;

        this.#setOpen(false, { persist: true, focus: false });
        this.toggles[0]?.focus();
      };

      #onDocumentClick = (event) => {
        if (!this.open) return;
        if (this.contains(event.target)) return;
        if (event.target.closest("[data-facet-panel-toggle]")) return;

        this.#setOpen(false, { persist: true, focus: false });
      };

      #setOpen(open, { persist, focus }) {
        this.hidden = !open;
        this.toggles.forEach((toggle) =>
          toggle.setAttribute("aria-expanded", String(open)),
        );

        if (persist) {
          try {
            if (open) {
              sessionStorage.setItem(
                FacetPanel.storageKey,
                window.location.pathname,
              );
            } else {
              sessionStorage.removeItem(FacetPanel.storageKey);
            }
          } catch (error) {
            /* Private mode: state is simply not persisted. */
          }
        }

        if (open && focus) {
          this.querySelector(
            'input, button, a, [tabindex]:not([tabindex="-1"])',
          )?.focus();
        }
      }
    },
  );
}
